// Pestaña Productos: cuánto aguanta cada producto abierto o elaborado y cómo
// se conserva. Las propuestas (por grupo) están sin confirmar hasta que cocina
// las revisa; cambiar cualquier valor guarda al momento y confirma (fuente
// "Cocina"); "Confirmar" sin cambiar nada conserva la fuente (fabricante o
// APPCC). Exporta también FuenteProducto (chip de la fuente del plazo), que
// usan la pestaña Etiquetar y la ficha de Ingredientes.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RotateCcw, Search, X } from 'lucide-react';
import { etiquetasAPI, textoErrorApi } from '../../api/api';
import { toast } from '../../hooks/use-toast';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { AVISO_SIMULACION } from '../../context/VistaEmpleadoContext';
import {
  AVISO_SUPERA_APPCC, CLASE_FUENTE, CONSERVACIONES, FUENTES_FILTRO, NOMBRE_CONSERVACION, NOMBRE_FUENTE,
  TEXTO_HASTA_ENVASE, VIDA_MODO_DIAS, VIDA_MODO_ENVASE, claveFuente, claveProducto, coincideBusqueda, infoFuente,
} from '../../utils/etiquetas';

const MAX_DIAS = 3650;
const MAX_HORAS = 720;

const claseSegmento = (activo) => `h-11 px-4 text-sm font-medium whitespace-nowrap transition ${activo
  ? 'bg-duetto-primary text-white'
  : 'bg-white text-gray-700 hover:bg-gray-50'}`;

const errorDe = (e, porDefecto) => textoErrorApi(e?.response?.data?.detail, porDefecto);

const nombreOrden = (a, b) => String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es');

// ==================== FUENTE DEL PLAZO ====================

// Chip con la fuente del plazo (Fabricante, APPCC Duetto, Cocina o Propuesta) y
// debajo, en pequeño, el texto literal o la regla (con enlace a la ficha del
// fabricante si lo hay).
export const FuenteProducto = ({ producto, className = '' }) => {
  if (!producto) return null;
  const info = infoFuente(producto);
  return (
    <div className={className} data-testid="producto-fuente">
      <span
        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${CLASE_FUENTE[info.clave]}`}
        title={info.detalle || undefined}
      >
        {info.nombre}
      </span>
      {(info.detalle || info.url) && (
        <p className="mt-0.5 text-[11px] leading-snug text-gray-600">
          {info.detalle}
          {info.url && (
            <>
              {info.detalle ? ' ' : ''}
              <a
                href={info.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-duetto-primary underline hover:no-underline"
              >
                ver ficha
              </a>
            </>
          )}
        </p>
      )}
    </div>
  );
};

// ==================== FILA ====================

const FilaProducto = ({ producto, editable, onGuardar }) => {
  const esPlato = producto.tipo === 'plato';
  const vidaActual = esPlato ? producto.vida_horas : producto.vida_dias;
  const textoInicial = vidaActual === null || vidaActual === undefined ? '' : String(vidaActual);
  const modoGuardado = esPlato ? VIDA_MODO_DIAS : (producto.vida_modo || VIDA_MODO_DIAS);
  const [vidaTexto, setVidaTexto] = useState(textoInicial);
  // Modo elegido en pantalla y aún sin guardar: al pasar de "hasta el envase" a
  // días hay que poner cuántos antes de guardar (null = el guardado)
  const [modoLocal, setModoLocal] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const clave = claveProducto(producto);
  const modo = modoLocal || modoGuardado;
  const pendienteDias = !esPlato && modo === VIDA_MODO_DIAS && modoGuardado === VIDA_MODO_ENVASE;

  useEffect(() => {
    setVidaTexto(textoInicial);
  }, [textoInicial]);

  useEffect(() => {
    setModoLocal(null);
  }, [modoGuardado]);

  const guardarCambios = useCallback(async (cambios) => {
    setGuardando(true);
    try {
      await onGuardar(producto, cambios);
    } catch (e) {
      setVidaTexto(textoInicial);
      setModoLocal(null);
      toast({ title: 'No se pudo guardar', description: errorDe(e, 'Inténtalo otra vez.'), variant: 'destructive' });
    } finally {
      setGuardando(false);
    }
  }, [onGuardar, producto, textoInicial]);

  const cambiarModo = useCallback((valor) => {
    if (valor === modoGuardado) {
      setModoLocal(null);
      setVidaTexto(textoInicial);
    } else if (valor === VIDA_MODO_ENVASE) {
      guardarCambios({ vida_modo: VIDA_MODO_ENVASE });
    } else {
      setModoLocal(VIDA_MODO_DIAS); // se guarda al poner los días
    }
  }, [modoGuardado, textoInicial, guardarCambios]);

  const guardarVida = useCallback(() => {
    const limpio = String(vidaTexto).trim();
    if (limpio === textoInicial) return;
    const n = Number(limpio);
    const maximo = esPlato ? MAX_HORAS : MAX_DIAS;
    if (!limpio || !Number.isInteger(n) || n < 1 || n > maximo) {
      setVidaTexto(textoInicial);
      toast({
        title: 'Valor no válido',
        description: esPlato ? `Pon entre 1 y ${MAX_HORAS} horas.` : `Pon entre 1 y ${MAX_DIAS} días.`,
        variant: 'destructive',
      });
      return;
    }
    guardarCambios(esPlato ? { vida_horas: n } : { vida_modo: VIDA_MODO_DIAS, vida_dias: n });
  }, [vidaTexto, textoInicial, esPlato, guardarCambios]);

  return (
    <li className="bg-white rounded-xl border border-gray-200 p-3" data-testid="producto-fila">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-gray-900 leading-snug">{producto.nombre}</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {(producto.alergenos || []).map((a) => (
              <span key={`c-${a}`} className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] text-red-700">{a}</span>
            ))}
            {producto.sin_datos_alergenos && (
              <span className="rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700">Sin datos de alérgenos</span>
            )}
            {producto.sin_grupo && (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800">Sin grupo: revisa la vida útil</span>
            )}
          </div>
          {(producto.trazas || []).length > 0 && (
            <p className="mt-1 text-[11px] leading-snug text-orange-700">
              Puede contener: {producto.trazas.join(', ').toLowerCase()}
            </p>
          )}
          {producto.aviso_huevo && (
            <p className="mt-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
              Lleva huevo: el RD 1021/2022 limita a 24 h si no es estable
            </p>
          )}
          <FuenteProducto producto={producto} className="mt-1.5" />
        </div>
        <Badge
          variant="outline"
          className={producto.confirmado
            ? 'flex-shrink-0 border-emerald-200 bg-emerald-50 text-emerald-700'
            : 'flex-shrink-0 border-amber-200 bg-amber-50 text-amber-800'}
        >
          {producto.confirmado ? 'Confirmado' : 'Propuesta'}
        </Badge>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
        <label
          htmlFor={`conservacion-${clave}`}
          className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 text-xs sm:text-sm text-gray-500 sm:text-gray-600"
        >
          Abierto se conserva en
          <select
            id={`conservacion-${clave}`}
            value={producto.conservacion}
            onChange={(ev) => { const v = ev.target.value; if (v !== producto.conservacion) guardarCambios({ conservacion: v }); }}
            disabled={!editable || guardando}
            data-testid="producto-conservacion"
            className="h-11 w-40 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 disabled:opacity-60"
          >
            {CONSERVACIONES.map((c) => (
              <option key={c} value={c}>{NOMBRE_CONSERVACION[c]}</option>
            ))}
          </select>
        </label>

        {!esPlato && (
          <label
            htmlFor={`modo-${clave}`}
            className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 text-xs sm:text-sm text-gray-500 sm:text-gray-600"
          >
            Dura abierto
            <select
              id={`modo-${clave}`}
              value={modo}
              onChange={(ev) => cambiarModo(ev.target.value)}
              disabled={!editable || guardando}
              data-testid="producto-vida-modo"
              className="h-11 w-56 max-w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 disabled:opacity-60"
            >
              <option value={VIDA_MODO_DIAS}>Días</option>
              <option value={VIDA_MODO_ENVASE}>{TEXTO_HASTA_ENVASE}</option>
            </select>
          </label>
        )}

        {(esPlato || modo === VIDA_MODO_DIAS) && (
        <div className="flex items-center gap-1.5">
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            max={esPlato ? MAX_HORAS : MAX_DIAS}
            step={1}
            value={vidaTexto}
            onChange={(e) => setVidaTexto(e.target.value)}
            onBlur={guardarVida}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            disabled={!editable || guardando}
            className="h-11 w-20 bg-white border-gray-300 text-base"
            aria-label={esPlato ? 'Vida en horas' : 'Vida en días'}
            data-testid="producto-vida"
          />
          <span className="text-sm text-gray-600">{esPlato ? 'h' : 'días'}</span>
          {pendienteDias && (
            <span className="text-xs text-amber-800">Pon los días para guardar</span>
          )}
        </div>
        )}

        {!esPlato && (
          <label htmlFor={`varias-${clave}`} className="flex items-center gap-2 min-h-[44px] text-sm text-gray-700 cursor-pointer">
            <Checkbox
              id={`varias-${clave}`}
              checked={!!producto.varias_abiertas}
              onCheckedChange={(v) => guardarCambios({ varias_abiertas: v === true })}
              disabled={!editable || guardando}
              className="h-5 w-5 border-gray-400 data-[state=checked]:bg-duetto-primary data-[state=checked]:border-duetto-primary data-[state=checked]:text-white"
            />
            Varias abiertas
          </label>
        )}

        <label htmlFor={`etiqueta-${clave}`} className="flex items-center gap-2 min-h-[44px] text-sm text-gray-700 cursor-pointer">
          <Switch
            id={`etiqueta-${clave}`}
            checked={!!producto.se_etiqueta}
            onCheckedChange={(v) => guardarCambios({ se_etiqueta: !!v })}
            disabled={!editable || guardando}
            className="data-[state=checked]:bg-duetto-primary data-[state=unchecked]:bg-gray-300 [&>span]:bg-white"
          />
          Se etiqueta
        </label>

        {!producto.confirmado && editable && (
          <button
            type="button"
            onClick={() => guardarCambios({ mantener_fuente: true })}
            disabled={guardando}
            className="h-11 px-4 rounded-lg border border-duetto-primary text-duetto-primary text-sm font-semibold hover:bg-emerald-50 disabled:opacity-50"
            data-testid="producto-confirmar"
          >
            Confirmar
          </button>
        )}
        {guardando && <Loader2 className="w-4 h-4 animate-spin text-gray-400" aria-label="Guardando" />}
      </div>

      {producto.supera_appcc && (
        <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1" data-testid="producto-supera-appcc">
          {AVISO_SUPERA_APPCC}
        </p>
      )}
    </li>
  );
};

// ==================== PESTAÑA ====================

const ProductosTab = ({
  productos, gruposIngrediente, gruposPlato, puedeEditar, cargando, error, onReintentar,
  onProductoActualizado, onRecargar, soloLectura,
}) => {
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState(null);
  const [tipo, setTipo] = useState('ingrediente');
  const [conservacion, setConservacion] = useState('todas'); // 'todas' o una de CONSERVACIONES
  const [fuente, setFuente] = useState('todas'); // 'todas' o una de FUENTES_FILTRO
  const [confirmando, setConfirmando] = useState(null);
  // Productos tocados en esta visita: siguen a la vista aunque ya estén
  // confirmados (si no, desaparecerían del filtro "Sin confirmar" al editarlos)
  const [tocados, setTocados] = useState([]);

  const editable = !!puedeEditar && !soloLectura;
  const hayPropuestas = useMemo(() => productos.some((p) => !p.confirmado), [productos]);
  const estado = filtroEstado || (hayPropuestas ? 'sin_confirmar' : 'todos');

  const grupos = useMemo(() => {
    const visibles = productos.filter((p) => p.tipo === tipo
      && (estado === 'todos' || !p.confirmado || tocados.includes(claveProducto(p)))
      && (conservacion === 'todas' || p.conservacion === conservacion)
      && (fuente === 'todas' || claveFuente(p) === fuente)
      && coincideBusqueda(p.nombre, busqueda));
    const porGrupo = new Map();
    visibles.forEach((p) => {
      const g = p.grupo || 'Otros';
      if (!porGrupo.has(g)) porGrupo.set(g, []);
      porGrupo.get(g).push(p);
    });
    const orden = (tipo === 'plato' ? gruposPlato : gruposIngrediente) || [];
    const lista = [];
    orden.forEach((g) => {
      if (porGrupo.has(g)) {
        lista.push({ nombre: g, productos: porGrupo.get(g).sort(nombreOrden) });
        porGrupo.delete(g);
      }
    });
    porGrupo.forEach((items, g) => lista.push({ nombre: g, productos: items.sort(nombreOrden) }));
    return lista;
  }, [productos, tipo, estado, conservacion, fuente, tocados, busqueda, gruposIngrediente, gruposPlato]);

  const totalVisibles = useMemo(() => grupos.reduce((s, g) => s + g.productos.length, 0), [grupos]);

  const guardar = useCallback(async (producto, cambios) => {
    const esPlato = producto.tipo === 'plato';
    const actual = {
      conservacion: producto.conservacion,
      se_etiqueta: !!producto.se_etiqueta,
      vida_congelado_dias: producto.vida_congelado_dias,
    };
    if (esPlato) actual.vida_horas = producto.vida_horas;
    else {
      actual.vida_modo = producto.vida_modo || VIDA_MODO_DIAS;
      // En modo "hasta el envase" no lleva días (null: no se manda)
      actual.vida_dias = producto.vida_dias;
      actual.varias_abiertas = !!producto.varias_abiertas;
    }
    const body = { ...actual, ...cambios };
    Object.keys(body).forEach((k) => {
      if (body[k] === null || body[k] === undefined) delete body[k];
    });
    const { data } = await etiquetasAPI.guardarProducto(producto.tipo, producto.id, body);
    const clave = claveProducto(producto);
    setTocados((prev) => (prev.includes(clave) ? prev : [...prev, clave]));
    onProductoActualizado(data && data.id ? data : { ...producto, ...cambios, confirmado: true });
  }, [onProductoActualizado]);

  const confirmarGrupo = useCallback(async (nombre, items) => {
    if (!items.length) return;
    setConfirmando(nombre);
    try {
      const { data } = await etiquetasAPI.confirmar(items.map((p) => ({ tipo: p.tipo, producto_id: p.id })));
      const claves = items.map(claveProducto);
      setTocados((prev) => [...prev.filter((c) => !claves.includes(c)), ...claves]);
      const n = Number(data?.confirmados ?? items.length);
      toast({ title: n === 1 ? '1 producto confirmado' : `${n} productos confirmados` });
      await onRecargar();
    } catch (e) {
      toast({ title: 'No se pudo confirmar', description: errorDe(e, 'Inténtalo otra vez.'), variant: 'destructive' });
    } finally {
      setConfirmando(null);
    }
  }, [onRecargar]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Aquí se fija cuánto aguanta cada producto abierto y cómo se conserva. Las propuestas salen por grupo y
        están sin confirmar hasta que cocina las revisa.
      </p>
      {!puedeEditar && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Solo dirección y cocina pueden cambiar estos valores.
        </p>
      )}
      {puedeEditar && soloLectura && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{AVISO_SIMULACION}</p>
      )}

      <div className="relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar producto"
          className="h-11 pl-9 pr-10 bg-white border-gray-300 text-base rounded-xl"
          autoComplete="off"
          data-testid="productos-buscar"
        />
        {busqueda && (
          <button
            type="button"
            onClick={() => setBusqueda('')}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 inline-flex items-center justify-center text-gray-400 hover:text-gray-600"
            aria-label="Borrar la búsqueda"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden" role="group" aria-label="Estado">
          <button type="button" className={claseSegmento(estado === 'sin_confirmar')} onClick={() => setFiltroEstado('sin_confirmar')} aria-pressed={estado === 'sin_confirmar'}>
            Sin confirmar
          </button>
          <button type="button" className={`${claseSegmento(estado === 'todos')} border-l border-gray-300`} onClick={() => setFiltroEstado('todos')} aria-pressed={estado === 'todos'}>
            Todos
          </button>
        </div>
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden" role="group" aria-label="Tipo">
          <button type="button" className={claseSegmento(tipo === 'ingrediente')} onClick={() => setTipo('ingrediente')} aria-pressed={tipo === 'ingrediente'}>
            Ingredientes
          </button>
          <button type="button" className={`${claseSegmento(tipo === 'plato')} border-l border-gray-300`} onClick={() => setTipo('plato')} aria-pressed={tipo === 'plato'}>
            Platos
          </button>
        </div>
        <div className="inline-flex max-w-full overflow-x-auto rounded-lg border border-gray-300" role="group" aria-label="Abierto se conserva en">
          {['todas', ...CONSERVACIONES].map((c, i) => (
            <button
              key={c}
              type="button"
              className={`${claseSegmento(conservacion === c)}${i > 0 ? ' border-l border-gray-300' : ''}`}
              onClick={() => setConservacion(c)}
              aria-pressed={conservacion === c}
              data-testid={`productos-conservacion-${c}`}
            >
              {c === 'todas' ? 'Todas' : NOMBRE_CONSERVACION[c]}
            </button>
          ))}
        </div>
        <div className="inline-flex max-w-full overflow-x-auto rounded-lg border border-gray-300" role="group" aria-label="Fuente del plazo">
          {['todas', ...FUENTES_FILTRO].map((f, i) => (
            <button
              key={f}
              type="button"
              className={`${claseSegmento(fuente === f)}${i > 0 ? ' border-l border-gray-300' : ''}`}
              onClick={() => setFuente(f)}
              aria-pressed={fuente === f}
              data-testid={`productos-fuente-${f}`}
            >
              {f === 'todas' ? 'Todas' : (f === 'appcc' ? 'APPCC' : NOMBRE_FUENTE[f])}
            </button>
          ))}
        </div>
      </div>

      {cargando && !productos.length && (
        <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          Cargando productos...
        </div>
      )}

      {error && !productos.length && !cargando && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 space-y-2">
          <p>{error}</p>
          <button
            type="button"
            className="h-11 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700"
            onClick={onReintentar}
          >
            <RotateCcw className="w-4 h-4" />
            Reintentar
          </button>
        </div>
      )}

      {productos.length > 0 && totalVisibles === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-600">
          {estado === 'sin_confirmar'
            ? 'No queda nada sin confirmar aquí. Pulsa Todos para ver el resto.'
            : (conservacion !== 'todas' || fuente !== 'todas'
              ? 'No hay productos con ese nombre y esos filtros.'
              : 'No hay productos con ese nombre.')}
        </div>
      )}

      {grupos.map((g) => {
        const propuestas = g.productos.filter((p) => !p.confirmado);
        return (
          <section key={g.nombre} className="space-y-2" data-testid="productos-grupo">
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              <h3 className="text-base font-semibold text-gray-900">
                {g.nombre} <span className="text-sm font-normal text-gray-500">{g.productos.length}</span>
              </h3>
              {editable && propuestas.length > 0 && (
                <button
                  type="button"
                  onClick={() => confirmarGrupo(g.nombre, propuestas)}
                  disabled={confirmando === g.nombre}
                  className="h-11 inline-flex items-center gap-1.5 rounded-lg bg-duetto-primary px-4 text-sm font-semibold text-white hover:bg-duetto-primary/90 disabled:opacity-50"
                  data-testid="productos-confirmar-grupo"
                >
                  {confirmando === g.nombre && <Loader2 className="w-4 h-4 animate-spin" />}
                  {propuestas.length === 1 ? 'Confirmar la propuesta' : `Confirmar las ${propuestas.length} propuestas`}
                </button>
              )}
            </div>
            <ul className="space-y-2">
              {g.productos.map((p) => (
                <FilaProducto key={claveProducto(p)} producto={p} editable={editable} onGuardar={guardar} />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

export default ProductosTab;
